/**
 * @description Shopify functions
 */

import { 
  asMoney, 
  wait, 
  waitForElement, 
} from './helpers';

import { md5 } from 'js-md5';

import Alpine from 'alpinejs';

/**
 * Extends the Shopify object with additional routes.
 *
 * @type {Object}
 */
Shopify.routes.shop_url = `${document.location.origin}`;

/**
 * Extends the Shopify object with helper functions.
 *
 * @type {Object}
 */
Shopify.helpers = {
  asMoney: asMoney,
  wait: wait,
  waitForElement: waitForElement,
  md5: md5,
};

/**
 * Dispatches a custom event with a payload.
 *
 * @param {String} event - The name of the event to dispatch.
 * @param {Object} payload - The payload to include with the event.
 */
function dispatch(event, payload) {
  window.dispatchEvent(
    new CustomEvent(event, {
      detail: payload,
    })
  );
}

/**
 * Adds a product to the cart.
 *
 * @link https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
 *
 * @param {Number} id - The variant ID to add to the cart.
 * @param {Number} quantity - The quantity of the variant to add to the cart.
 * @returns {Promise<Object>} A promise that:
 *  - resolves with an object containing the added item and cart data.
 *  - rejects with an error if the request fails.
 * @fires cart:adding
 * @fires cart:added
 * @fires cart:add:error
 */
function add(id, quantity = 1, selling_plan = null) {
  let payload = {
    id,
    quantity,
    selling_plan
  };

  dispatch('cart:adding', payload);

  return new Promise((resolve, reject) => {
    fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((item) => {
        // the item has been added, but we now need to refresh the cart
        Shopify.cart
          .refresh()
          .then(() => {
            // there's a chance that the amount in the cart has exceeded the max, so we need to call change
            Shopify.cart
              .change(item.key, item.quantity)
              .then(({ item, cart }) => {
                dispatch('cart:added', item);
                resolve({ item, cart });
              })
              .catch((error) => {
                // Need to reword this error
                if (error.message.includes('due to availability.')) {
                  error.message = 'You have the maximum amount of this item in your cart.';
                }

                reject(error);
              });
          })
          .catch((error) => {
            reject(error);
          });
      })
      .catch((error) => {
        dispatch('cart:add:error', error);
        reject(error);
      });
  });
}

/**
 * Refreshes the cart by fetching the latest cart data from the server.
 *
 * @link https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 *
 * @returns {Promise<Object>} A promise that:
 *  - resolves with the refreshed cart data,
 *  - rejects with an error if the request fails.
 * @fires cart:refreshing
 * @fires cart:refreshed
 * @fires cart:refresh:error
 */
function refresh() {
  dispatch('cart:refreshing');

  return new Promise((resolve, reject) => {
    fetch(`${Shopify.routes.root}cart.js`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((cart) => {
        dispatch('cart:refreshed', cart);
        Alpine.store('cart', cart);
        resolve(cart);
      })
      .catch((error) => {
        dispatch('cart:refresh:error', error);
        reject(error);
      });
  });
}

/**
 * Changes the quantity of an item in the cart by sending a POST request to the server.
 *
 * @link https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
 *
 * @param {mixed} id - The Line Item ID (as string), or ID (as number) of the item to change. Defaults to 1.
 * @param {number} quantity - The new quantity of the item.
 * @returns {Promise<Object>} A promise that:
 *  - resolves with an object containing the changed item and cart data.
 *  - rejects with an error if the request fails.
 * 
 * @fires cart:changing
 * @fires cart:changed
 * @fires cart:change:error
 */
function change(id, quantity = 1) {
  let payload = {
    id,
    quantity,
  };

  dispatch('cart:changing', payload);

  return new Promise((resolve, reject) => {
    fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        dispatch('cart:changed', response);
        // 422 indicates a stock error
        if (response.status === 422) {
          reject(response);
        }

        // refresh the cart
        Shopify.cart
          .refresh()
          .then((cart) => {
            resolve(cart);
          })
          .catch((error) => {
            reject(error);
          });
      })
      .catch((error) => {
        dispatch('cart:change:error', error);
        reject(error);
      });
  });
}

/**
 * Removes an item from the cart by calling `Shopify.Cart.change` with a quantity of 0.
 *
 * @link https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-clear-js
 *
 * @param {mixed} id - The Line Item ID (as string), or ID (as number) of the item to remove.
 * @returns {Promise<Object>} A promise that:
 *  - resolves with an object containing the changed item and cart data.
 *  - rejects with an error if the request fails.
 * 
 * @see Shopify.cart.change
 */
function remove(id) {
  return Shopify.cart.change(id, 0);
}

/**
 * Clears the cart by sending a POST request to the server.
 *
 * @link https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-clear-js
 *
 * @returns {Promise<Object>} A promise that:
 *  - resolves with the cleared cart data.
 *  - rejects with an error if the request fails.
 * 
 * @fires cart:clearing
 * @fires cart:cleared
 * @fires cart:clear:error
 */
function clear() {
  dispatch('cart:clearing');

  return new Promise((resolve, reject) => {
    fetch(`${Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((cart) => {
        dispatch('cart:cleared', cart);
        resolve(cart);
      })
      .catch((error) => {
        dispatch('cart:clear:error', error);
        reject(error);
      });
  });
}

/**
 * Helper to build and validate shipping rates query string.
 *
 * @param {string} zip
 * @param {string} country
 * @param {string} province
 * @returns {string} query string
 * @throws {Error} if no parameters provided
 */
function buildShippingQueryString(zip, country, province) {
  const query = {};
  if (zip) {
    query['shipping_address[zip]'] = zip;
  }
  if (country) {
    query['shipping_address[country]'] = country;
  }
  if (province) {
    query['shipping_address[province]'] = province;
  }
  if (Object.keys(query).length === 0) {
    throw new Error('At least one of zip, country, or province must be provided.');
  }
  return new URLSearchParams(query).toString();
}

function prepareShippingRates(zip, country, province) {
  let queryString;
  try {
    queryString = buildShippingQueryString(zip, country, province);
  } catch (error) {
    return Promise.reject(error);
  }

  return new Promise((resolve, reject) => {
    fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json?${queryString}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        resolve(data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function asyncShippingRates(zip, country, province) {
  let queryString;

  try {
    queryString = buildShippingQueryString(zip, country, province);
  } catch (error) {
    return Promise.reject(error);
  }

  return new Promise((resolve, reject) => {
    fetch(`${Shopify.routes.root}cart/async_shipping_rates.json?${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            reject(new Error(`HTTP error ${response.status}: ${response.statusText}`, { cause: errorData }));
          });
        }
        return response.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

// Initialize any events or listeners here
function initEvents() {
  // Example: Listen for cart refresh events to log the cart data
  window.addEventListener('cart:refresh', (event) => {
    refresh();
  });

   window.addEventListener('cart:change' , (event) => {
    const { id, quantity } = event.detail;
    change(id, quantity);
  });

  window.addEventListener('cart:add' , (event) => {
    const { id, quantity } = event.detail;
    add(id, quantity);
  });

  window.addEventListener('cart:remove' , (event) => {
    const { id } = event.detail;
    remove(id);
  });
}

initEvents();

/**
 * Extends the Shopify object with cart functions.
 *
 * @type {Object}
 */
Shopify.cart = {
  add: add,
  change: change,
  remove: remove,
  clear: clear,
  refresh: refresh,
  prepareShippingRates: prepareShippingRates,
  asyncShippingRates: asyncShippingRates,
};
